// ═══════════════════════════════════════════════════════════════
// Perimeter Module — Weather Utilities (NWS API)
// 1:1 port from perimeter-embedded.html fetchWeather
// ═══════════════════════════════════════════════════════════════

import { DIR_ORDER } from './perimeterConstants';
import { primary, semantic } from '../lib/colors';

// ── Types ────────────────────────────────────────────────────

export interface CurrentWeather {
  temperature: number | null;
  windSpeed: number | null;
  windDirection: string;
  windDegrees: number | null;
  humidity: number | null;
  description: string;
  visibility: string | null;
  pressure: string | null;
  dewpoint: number | null;
  heatIndex: number | null;
  windChill: number | null;
  timestamp: string;
  station: string;
}

export interface ForecastPeriod {
  name: string;
  temperature: number;
  temperatureUnit: string;
  shortForecast: string;
  detailedForecast: string;
  windSpeed: string;
  windDirection: string;
  icon: string;
  isDaytime: boolean;
  number: number;
  startTime: string;
  endTime: string;
}

export interface WeatherAlert {
  id: string;
  event: string;
  severity: string;
  headline: string;
  description: string;
  instruction: string;
  onset: string;
  expires: string;
}

export interface WeatherData {
  current: CurrentWeather | null;
  forecast: ForecastPeriod[];
  alerts: WeatherAlert[];
}

// ── Fetch Weather ────────────────────────────────────────────

export async function fetchWeather(lat: number, lng: number): Promise<WeatherData> {
  const hdr: Record<string, string> = {
    'User-Agent': 'PerimeterApp (opengov-suite)',
    'Accept': 'application/geo+json',
  };

  // Step 1: Get grid point info
  const pt = await (
    await fetch(`https://api.weather.gov/points/${lat},${lng}`, { headers: hdr })
  ).json();

  const gridId: string = pt.properties.gridId;
  const gx: number = pt.properties.gridX;
  const gy: number = pt.properties.gridY;
  const stationsUrl: string = pt.properties.observationStations;

  // Step 2: Fetch forecast, alerts, and stations in parallel
  const [fc, alerts, stations] = await Promise.all([
    fetch(`https://api.weather.gov/gridpoints/${gridId}/${gx},${gy}/forecast`, { headers: hdr }).then(r => r.json()),
    fetch(`https://api.weather.gov/alerts/active?point=${lat},${lng}`, { headers: hdr }).then(r => r.json()),
    fetch(stationsUrl, { headers: hdr }).then(r => r.json()),
  ]);

  // Step 3: Get current observations from nearest station
  const stationId: string | undefined = stations.features?.[0]?.properties?.stationIdentifier;
  let current: CurrentWeather | null = null;

  if (stationId) {
    const obs = await (
      await fetch(`https://api.weather.gov/stations/${stationId}/observations/latest`, { headers: hdr })
    ).json();
    const p = obs.properties;

    const cToF = (c: number | null | undefined): number | null =>
      c != null ? Math.round(c * 9 / 5 + 32) : null;

    current = {
      temperature: cToF(p.temperature?.value),
      windSpeed: p.windSpeed?.value ? Math.round(p.windSpeed.value * 0.621371) : null,
      windDirection: p.windDirection?.value != null
        ? DIR_ORDER[Math.round(p.windDirection.value / 45) % 8]
        : '',
      windDegrees: p.windDirection?.value ?? null,
      humidity: p.relativeHumidity?.value ? Math.round(p.relativeHumidity.value) : null,
      description: p.textDescription || '',
      visibility: p.visibility?.value ? (p.visibility.value * 0.000621371).toFixed(1) : null,
      pressure: p.barometricPressure?.value
        ? (p.barometricPressure.value * 0.00029530).toFixed(2)
        : null,
      dewpoint: cToF(p.dewpoint?.value),
      heatIndex: cToF(p.heatIndex?.value),
      windChill: cToF(p.windChill?.value),
      timestamp: p.timestamp || '',
      station: stationId,
    };
  }

  return {
    current,
    forecast: (fc.properties?.periods || []).slice(0, 8) as ForecastPeriod[],
    alerts: (alerts.features || []).map((f: { id: string; properties: Record<string, unknown> }) => ({
      id: f.id,
      event: f.properties.event as string,
      severity: f.properties.severity as string,
      headline: f.properties.headline as string,
      description: f.properties.description as string,
      instruction: f.properties.instruction as string,
      onset: f.properties.onset as string,
      expires: f.properties.expires as string,
    })),
  };
}

// ── Alert Severity Color ─────────────────────────────────────

export function alertSeverityColor(severity: string): string {
  const map: Record<string, string> = {
    Extreme: semantic.error,
    Severe: '#DF461B',
    Moderate: '#A67103',
    Minor: primary[700],
  };
  return map[severity] || '#7B7D7F';
}
